// Helpers for Sentry.init({ beforeSend }) in src/index.js.
//
// Our production bundle is always served from /static/js/ (CRA build). Browser
// extensions and browser-injected user scripts (Chrome for iOS injects its
// __gCrWeb autofill/password/translate scripts into every page) throw errors
// that WebKit reports against the page URL, e.g. "/settings:226:63", with no
// JS file at all. Those are not our bugs and cannot be source-mapped
// (VERSTAANI-FRONTEND-C/R/S/T/V/W/X/Y/Z/10/11).

const OWN_BUNDLE_PATH = '/static/js/';

const EXTENSION_SCHEMES = ['chrome-extension://', 'moz-extension://', 'safari-extension://'];

/** All stack frames across every exception value (Error.cause chains included). */
export function collectFrames(event) {
  const values = (event && event.exception && event.exception.values) || [];
  const frames = [];
  values.forEach((value) => {
    const valueFrames = value && value.stacktrace && value.stacktrace.frames;
    if (Array.isArray(valueFrames)) {
      frames.push(...valueFrames);
    }
  });
  return frames;
}

function frameFilename(frame) {
  return frame && typeof frame.filename === 'string' ? frame.filename : '';
}

/** True when any frame comes from an injected browser extension. */
export function isBrowserExtensionEvent(event) {
  return collectFrames(event).some((frame) => {
    const filename = frameFilename(frame);
    return EXTENSION_SCHEMES.some((scheme) => filename.includes(scheme));
  });
}

/**
 * True when the event has a stack trace but none of its frames point at our
 * bundle. Events with no frames at all (captureMessage, non-Error rejections)
 * are kept so we never hide something we cannot classify.
 */
export function isForeignScriptEvent(event) {
  const frames = collectFrames(event);
  if (frames.length === 0) {
    return false;
  }
  return !frames.some((frame) => frameFilename(frame).includes(OWN_BUNDLE_PATH));
}

// Registration numbers and VINs must never reach Sentry (Traficom register terms). Same patterns
// as the backend's config/sentry_filters.py: hyphenated plates in every Finnish format, bare
// plates only as 2-3 letters + 1-3 digits ("ABC123"), and 17-character VINs.
const NOT_ALNUM_BEFORE = '(?<![A-ZÅÄÖ0-9])';
const NOT_ALNUM_AFTER = '(?![A-ZÅÄÖ0-9])';
const PLATE_RE = new RegExp(
  `${NOT_ALNUM_BEFORE}(?:[A-ZÅÄÖ]{1,3}-[0-9]{1,5}|[0-9]{1,3}-[A-ZÅÄÖ]{2,3}|[A-ZÅÄÖ]{2,3}[0-9]{1,3}|ÅL[0-9]{1,5})${NOT_ALNUM_AFTER}`,
  'gi',
);
const VIN_RE = new RegExp(`${NOT_ALNUM_BEFORE}[A-HJ-NPR-Z0-9]{17}${NOT_ALNUM_AFTER}`, 'gi');
const FILTERED = '[Filtered]';

// Code locations, not user data: left alone so stack traces stay readable.
const SCRUB_SKIP_KEYS = new Set([
  'filename', 'abs_path', 'module', 'function', 'context_line', 'pre_context', 'post_context',
  'lineno', 'colno', 'in_app', 'event_id', 'timestamp', 'platform', 'level', 'release',
  'environment', 'sdk',
]);

export function maskVehicleIdentifiers(text) {
  return text.replace(VIN_RE, FILTERED).replace(PLATE_RE, FILTERED);
}

const MAX_SCRUB_DEPTH = 20;

function scrub(value, depth) {
  if (typeof value === 'string') {
    return maskVehicleIdentifiers(value);
  }
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (depth > MAX_SCRUB_DEPTH) {
    return FILTERED;
  }
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) {
      value[i] = scrub(value[i], depth + 1);
    }
    return value;
  }
  // Breadcrumb data can hold raw console arguments: DOM nodes, Errors, class instances. Their
  // string form is what Sentry would show anyway; walking them could loop or take forever.
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) {
    return maskVehicleIdentifiers(String(value));
  }
  Object.keys(value).forEach((key) => {
    if (SCRUB_SKIP_KEYS.has(key)) {
      return;
    }
    const item = scrub(value[key], depth + 1);
    const maskedKey = maskVehicleIdentifiers(key);
    if (maskedKey !== key) {
      delete value[key];
    }
    value[maskedKey] = item;
  });
  return value;
}

/** Mask registration numbers and VINs in every string of a Sentry event or breadcrumb, in place. */
export function scrubVehicleIdentifiers(value) {
  return scrub(value, 0);
}
